import React, { lazy, Suspense } from 'react';
import { Link } from 'react-router-dom';
import Canonical from '../../seo/Canonical';
import Footer from '../Layouts/Footer';
import './PricingPage.css';
import PricingPageHero from './PricingPageHero';
import '../Homepage/FAQ.css';
import RelatedLinks from '../Common/RelatedLinks';
import FAQSection from '../FAQSection';
import FbaPrepCostBreakdown from '../AmazonFBA/FbaPrepCostBreakdown';
import FbaAmazonCostComparison from '../AmazonFBA/FbaAmazonCostComparison';
import FbaPrepPricingCta from '../AmazonFBA/FbaPrepPricingCta';
import { FBA_PREP_PRICING_FAQS } from '../../data/fbaPrepPricingData';
import { PRICING_RELATED_BLOGS } from '../../data/resourcePageRelatedBlogs';

const FBAPricing = lazy(() => import('../AmazonFBA/Fbapricing'));
const FBAPricingTiers = lazy(() => import('../AmazonFBA/FBAPricingTiers'));

const PricingPage: React.FC = () => {
  const faqs = [
    {
      q: 'How are fulfilment prices calculated?',
      a: 'Pricing is based on pick/pack, additional items, packaging materials, storage and courier label. We model your basket and parcel sizes to forecast landed cost accurately.',
    },
    {
      q: 'Do you charge setup fees or long contracts?',
      a: 'No long lock‑ins. Setup is typically free for standard integrations. We operate rolling monthly terms.',
    },
    {
      q: 'Can I get volume discounts?',
      a: 'Yes. Tiers reduce per‑order costs as your monthly volume grows. Speak to us for a custom quote.',
    },
    {
      q: 'Are packaging materials included?',
      a: 'Materials can be bundled or itemised depending on your preference. We recommend right‑sizing to lower postage bands.',
    },
    {
      q: 'What about returns handling fees?',
      a: 'Returns are scanned and graded within 24–48h. Fees are itemised per unit for transparency and control.',
    },
    {
      q: 'Do you support Amazon SFP rate compliance?',
      a: 'Yes—labels are purchased via Amazon Buy Shipping and tracking uploads immediately to protect performance metrics.',
    },
    ...FBA_PREP_PRICING_FAQS.slice(0, 3).map(({ question, answer }) => ({ q: question, a: answer })),
  ];

  const longCopy = (
    <div style={{ color: '#334155', lineHeight: 1.78, display: 'grid', gap: 14 }}>
      <p>
        We believe fulfilment pricing should be simple, predictable and easy to understand. Every quote clearly
        itemises{' '}
        <Link to="/blog/pick-and-pack-fulfilment-uk-explained">pick and pack fulfilment</Link>, storage,
        packaging materials, courier services and any additional handling — the same transparency we explain in our{' '}
        <Link to="/blog/fba-prep-centre-uk-pricing-2026">FBA prep centre UK pricing</Link> guide. As volumes grow,
        tiered rates reduce cost per order; compare models in{' '}
        <Link to="/blog/3pl-vs-in-house-fulfillment">3PL vs in-house fulfilment</Link> and{' '}
        <Link to="/blog/fba-vs-fbm-vs-3pl-which-is-best-reddit-guide-uk">FBA vs FBM vs 3PL</Link>.
      </p>
      <p>
        For Amazon sellers, model prep separately with{' '}
        <Link to="/blog/fba-prep-cost-uk-2026-pricing-guide-reddit-insights">FBA prep cost UK 2026</Link> and{' '}
        <Link to="/blog/amazon-fba-prep-service-fees-explained">Amazon FBA prep service fees explained</Link>, then
        reduce FC storage drag via{' '}
        <Link to="/blog/reduce-amazon-fba-storage-fees-drip-feed">how to reduce Amazon FBA storage fees</Link>. Courier
        bands and SLAs sit alongside{' '}
        <Link to="/blog/3pl-logistics-uk-explained-2026">3PL logistics UK explained</Link>.
      </p>
      <p>
        Prefer a full ops map first? Read the{' '}
        <Link to="/blog/ecommerce-fulfilment-guide-uk">ecommerce fulfilment guide UK</Link>,{' '}
        <Link to="/blog/how-to-outsource-ecommerce-fulfilment">how to outsource ecommerce fulfilment</Link>, and{' '}
        <Link to="/blog/best-3pl-features-for-brands">best 3PL features for brands</Link> — then use the calculator
        above for a line-item estimate.
      </p>
    </div>
  );

  return (
    <div className="pricing-page logistics-page">
      <Canonical baseUrl="https://www.pickpackpro.co.uk" />

      <PricingPageHero />

      <Suspense fallback={null}>
        <FBAPricing
          showQuickEstimate
          headingTitle="Calculate Your Fulfilment & Amazon FBA Prep Costs"
          headingSubtitle="Estimate your Amazon FBA prep, pick and pack, and fulfilment costs in minutes. Adjust your monthly order volume, product type, and service requirements to compare pricing before requesting a bespoke quote. Our interactive calculator provides a clear cost estimate, helping you budget confidently and choose the fulfilment solution that best fits your business."
        />
      </Suspense>

      <FbaPrepCostBreakdown />

      <div className="pricing-cta-spacer">
        <FbaPrepPricingCta
          label="FBA costs"
          title="Need a Fulfilment Quote That Includes Amazon FBA Prep?"
          subtitle="Receive a bespoke quote combining Amazon FBA prep, pick and pack, storage, and shipping into one transparent, competitive rate card with no hidden fees."
        />
      </div>

      <div className="pricing-container">
        <section>
          <Suspense fallback={null}>
            <FBAPricingTiers />
          </Suspense>
        </section>

        <FbaAmazonCostComparison />

        <section className="pricing-copy">{longCopy}</section>
      </div>

      <FAQSection
        headingId="pricing-faq"
        className="faqs-section"
        title="Frequently Asked Questions"
        faqs={faqs.map(({ q, a }) => ({ question: q, answer: a }))}
      />

      <RelatedLinks
        title="Related pricing & fulfilment guides"
        serviceLinks={PRICING_RELATED_BLOGS}
        blogLink={{ to: '/fulfillment-blog', label: 'All fulfilment blog articles' }}
      />

      <Footer />
    </div>
  );
};

export default PricingPage;
